<template>
    <v-layout row>
        <v-flex xs12 sm12>
            <v-card class="elavation-1 transparent">
                <v-toolbar class="transparent" flat height="37">
                    <v-btn dark icon @click="$router.go(-1)">
                        <v-icon>arrow_back</v-icon>
                    </v-btn>
                    <v-spacer></v-spacer>
                    <help-link appId="finance" pageId="cash-advances-self-service" color="white"></help-link>
                    <div v-if="cashadvances_detail">
                        <v-menu v-if="!loading" left v-model="menu_status" offset-y :disabled="disabled_params"> 
                            <template v-slot:activator="{ on }">
                                <v-btn :color="commonfn.getStatusButtonColor(cashadvances_detail.status)" dark v-on="on" :loading="loading_status">
                                    &nbsp;&nbsp;&nbsp;{{commonfn.getStatusDescription(cashadvances_detail.status)}}&nbsp;&nbsp;
                                    <v-icon v-show="!menu_status">arrow_drop_down</v-icon>
                                    <v-icon v-show="menu_status">arrow_drop_up</v-icon>
                                </v-btn>
                            </template>
                            <v-list dense >
                                <v-list-tile v-show="hasEditRights&&(created_status||rejected_status)" @click="showEdit" :key="1" title="Edit Cash Advances">
                                    <v-list-tile-title small>Edit</v-list-tile-title>
                                </v-list-tile>
                                <v-list-tile v-show="hasEditRights&&(created_status||rejected_status)" @click="setForReview" :key="2" title="Set to For Review">
                                    <v-list-tile-title small>For Review</v-list-tile-title>
                                </v-list-tile>
                            </v-list>
                        </v-menu>
                    </div>
                </v-toolbar>

                <v-progress-linear v-if="loading" indeterminate color="primary" height="3" style="margin:1px -3px 0px -3px;"></v-progress-linear>
            </v-card>
            
            <!-- details -->
            <v-expansion-panel v-if="!loading" v-model="panel" expand style="margin-top:5px;">
                <!-- employee details -->
                <v-expansion-panel-content>
                    <template v-slot:header>
                        <span class="font-weight-bold">SUMMARY</span>
                        <v-spacer></v-spacer>
                    </template>
                    <ca-summary :cashadvances_detail="cashadvances_detail" :viewedByOwner="true"></ca-summary>
                </v-expansion-panel-content>

                <!-- payment monthly details -->
                <v-expansion-panel-content>
                    <template v-slot:header>
                        <span class="font-weight-bold">PAYMENT SCHEDULE</span>
                        <v-spacer></v-spacer>
                    </template>
                    <v-data-table
                        :headers="headers"
                        :items="cashadvances_schedule"
                        :rows-per-page-items="rowsPerPageItems"
                        :pagination.sync="paginationSync"
                    >
                        <v-progress-linear v-slot:progress color="primary" indeterminate></v-progress-linear>
                        <template v-slot:items="props">
                            <tr>
                                <td>{{ props.item.dueOn | moment('MMM DD, YYYY') }}</td>
                                <td>{{ numFormat.formatNumber(props.item.amountDue) }}</td>
                                <td>
                                    <span :class="commonfn.getStatusColor(props.item.status)">{{commonfn.getStatusDescription(props.item.status)}}</span>
                                </td>
                                <td>
                                    <span v-if="props.item.processedOn">
                                        {{ props.item.processedOn | moment('MMM DD, YYYY') }}
                                    </span>
                                </td>   
                            </tr>
                        </template>
                    </v-data-table> 
                </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Comments and Updates -->
            <v-layout row wrap>
                <v-flex xs12 sm6>
                    <!-- comments history component -->
                    <comments-history v-if="!loading" :comments_data="commentsData"></comments-history>
                </v-flex>
                <v-flex xs12 sm6>
                    <update-history v-if="!loading"  ref="updateHistoryRef" :page_query_id="selectedCashAdvancesId"></update-history>
                </v-flex>
            </v-layout>
        </v-flex>
        
        <v-dialog v-model="dialogAddEdit" width="700" persistent :fullscreen="isMobile">   
            <add-edit ref="addEditRef" :dialogAddEdit.sync="dialogAddEdit" @send="getCashAdvancesDetail(false)"></add-edit>
        </v-dialog>

        <!-- status messaging component -->
        <status-messaging :page_query_id="selectedCashAdvancesId" @refresh="updateHandler"></status-messaging>
    </v-layout> 
</template>

<script>

import commonfn from '@/common/functions'
import cashadvancesApi from '@/api/cashadvances'
import commonData from '@/common/data'
import numFormat from '@/common/num_format'
import pageKeys from '@/common/pagekeys'
import notificationTypes from '@/common/notificationTypes'
import userPrefs from '@/helpers/user-prefs'
import rowsPerPageItems from '@/constants/rows-per-page'

export default {     
    name: 'MyCashAdvances',
    metaInfo: {
        title: 'My Cash Advances',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'View, create My Cash Advances' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
            ]
    },
    components: {
        'help-link': () => import('@/components/Common/HelpLink'),
        'add-edit': () => import('@/components/CashAdvances/AddEditCashAdvances'),
        'ca-summary': () => import('@/components/CashAdvances/CashAdvancesSummary'),
        'status-messaging': () => import('@/components/Common/StatusMessaging'),
        'update-history': () => import('@/components/Common/UpdatesHistory'),
        'comments-history': () => import('@/components/Common/CommentsHistory')
    },   
    data () {
        const routeName = this.$route.name
        const savedSort = userPrefs.getSort(routeName)

        return {
            commonfn,
            numFormat,
            rowsPerPageItems,
            paginationSync: {
                rowsPerPage: userPrefs.getRowsPerPage(routeName),
                sortBy: savedSort.sortBy,
                descending: savedSort.descending,
            },
            common_data: null,
            error: false,
            errorMessage: '',
            loading: false,
            loading_status: false,
            menu_status: false,
            my_profile: null,
            headers: [
                {text: 'Due Date',align: 'left',value: 'dueOn', width: '15%', sortable:true},
                {text: 'Amount Due',align: 'left',value: 'amountDue', width: '15%', sortable:true},
                {text: 'Status',align: 'left',value: 'status',  width: '15%', sortable:true},
                {text: 'Processed On',align: 'left',value: 'processedOn',  width: '15%', sortable:true}
            ],
            dialogAddEdit: false,
            panel: [true, true],
            cashadvances_detail: null,
            cashadvances_schedule: []
        }
    },
    methods: {
        async setForReview() {
            try {
                this.loading_status = true
                var data = {
                    cashAdvancesId : this.selectedCashAdvancesId
                }
                const response = await cashadvancesApi.setForReview(data, pageKeys.my_cash_advances)
                if (response.status == 200) {
                    await this.getCashAdvancesDetail(false)
                }
                this.loading_status = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        showEdit() {
            this.$refs.addEditRef.setUpdate(this.cashadvances_detail, pageKeys.my_cash_advances)
            this.dialogAddEdit = true
        },
        async getCashAdvancesDetail(show_loading) {
            try {
                this.loading = show_loading
                const response = await cashadvancesApi.getMyCashAdvancesDetail(this.selectedCashAdvancesId)
                if (response.status == 200) {
                    this.cashadvances_detail = response.data
                    this.cashadvances_schedule = response.data.paymentSchedule
                }
                this.loading = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        updateHandler(data) {
            //exclude the the device/user who initiates the update
            if (data!=this.$store.state.userId) this.getCashAdvancesDetail(false)

            //invoke updateHistoryRef appendData 
            this.$refs.updateHistoryRef.getHistory()
        }
    },
    created() {
        this.common_data = commonData

        //update header titles
        this.$root.$emit('setHeaderTitles', {
            display: true,
            headerTitle: '',
            headerPageTitle: 'My Cash Advances Details'
        })

        this.getCashAdvancesDetail(true)
    },
    watch: {
        'paginationSync.rowsPerPage'(newVal) {
            userPrefs.updateRowsPerPage(this.$route.name, newVal)
        },
        'paginationSync.sortBy'(newVal) {
            userPrefs.updateSort(this.$route.name, newVal, this.paginationSync.descending)
        },
        'paginationSync.descending'(newVal) {
            userPrefs.updateSort(this.$route.name, this.paginationSync.sortBy, newVal)
        }
    },
    computed: {
        hasEditRights() {
            return commonfn.hasEditRights(this.$router.currentRoute.path)
        },
        selectedCashAdvancesId() {
            return this.$route.params.cashAdvancesId
        },
        commentsData() {
            if (this.cashadvances_detail!=null) {
                return {
                    pageQueryId: this.selectedCashAdvancesId,
                    pageItemCreatedBy: this.cashadvances_detail.userId,
                    pageLink: this.$router.currentRoute.path.replace('/mycashadvances/','/cashadvances/'), //should send to approver
                    pageKey: pageKeys.cash_advances,
                    notificationTypeId: notificationTypes.cash_advances
                }
            }
        },
        isMobile() {
            return window.innerWidth < 600
        },
        created_status() {
            if (this.cashadvances_detail!=null) return this.cashadvances_detail.status==commonfn.getStatusCode('Created')
        },
        forReview_status() {
            if (this.cashadvances_detail!=null) return this.cashadvances_detail.status==commonfn.getStatusCode('For Review')
        },
        approved_status() {
            if (this.cashadvances_detail!=null) return this.cashadvances_detail.status==commonfn.getStatusCode('Approved')
        },
        fundReleased_status() {
            if (this.cashadvances_detail!=null) return this.cashadvances_detail.status==commonfn.getStatusCode('Fund Released')
        },
        rejected_status() {
            if (this.cashadvances_detail!=null) return this.cashadvances_detail.status==commonfn.getStatusCode('Rejected')
        },
        deleted_status() {
            if (this.cashadvances_detail!=null) return this.cashadvances_detail.status==commonfn.getStatusCode('Deleted')
        },
        disabled_params(){
            return this.forReview_status||this.approved_status||this.fundReleased_status||this.deleted_status
        }
    }
}
</script>